/**
 * Client HTTP (credentials pour cookies JWT).
 * XMLHttpRequest évite les conflits avec un fetch global patché qui lit le corps
 * avant notre code (« body stream already read » sur res.text()).
 */

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export class HttpError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }

  /** Compatibilité avec l’ancien accès style axios */
  get response() {
    return { status: this.status, data: this.data };
  }
}

/**
 * Message affichable (évite de masquer err.message derrière un libellé générique quand le corps n’a pas `detail`).
 *
 * @param {unknown} err
 * @returns {string}
 */
export function httpErrorUserMessage(err) {
  if (err instanceof HttpError) {
    const m = typeof err.message === 'string' ? err.message.trim() : '';
    if (m) return m;
    return `Erreur serveur (${err.status}).`;
  }
  const m = err && typeof err.message === 'string' ? err.message.trim() : '';
  return m || 'Une erreur est survenue.';
}

/**
 * @param {string} url
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {object|FormData|string|null} [options.body] — objet sérialisé en JSON sauf FormData
 * @param {Record<string,string>} [options.headers]
 * @param {AbortSignal} [options.signal]
 * @param {number} [options.timeoutMs] — annule la requête après N ms
 * @returns {Promise<any>} corps JSON parsé
 */
export function httpRequest(url, options = {}) {
  const {
    method = 'GET',
    body,
    headers: extraHeaders = {},
    signal: userSignal,
    timeoutMs = 0,
  } = options;

  return new Promise((resolve, reject) => {
    if (userSignal?.aborted) {
      reject(Object.assign(new Error('Requete annulee'), { cause: userSignal.reason }));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.withCredentials = true;
    if (timeoutMs > 0) {
      xhr.timeout = timeoutMs;
    }

    const headers = { ...extraHeaders };
    let payload = null;
    if (body !== undefined && body !== null) {
      if (body instanceof FormData) {
        payload = body;
      } else if (typeof body === 'string') {
        payload = body;
      } else {
        payload = JSON.stringify(body);
        if (!headers['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }
      }
    }

    for (const [k, v] of Object.entries(headers)) {
      if (v == null || v === '') continue;
      xhr.setRequestHeader(k, String(v));
    }

    const onAbort = () => {
      xhr.abort();
    };
    if (userSignal) {
      userSignal.addEventListener('abort', onAbort);
    }

    let settled = false;
    const finish = () => {
      if (userSignal) {
        userSignal.removeEventListener('abort', onAbort);
      }
    };
    const settle = (fn) => {
      if (settled) return;
      settled = true;
      finish();
      fn();
    };

    xhr.onload = () => {
      settle(() => {
        const text = xhr.responseText;
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = text;
        }
        const status = xhr.status;
        if (status < 200 || status >= 300) {
          const detail =
            data && typeof data === 'object' && data !== null ? data.detail : undefined;
          let msg = formatDetail(detail);
          if (!msg && typeof data === 'string' && data.trim()) {
            msg = data.trim().slice(0, 400);
          }
          if (!msg) msg = xhr.statusText || `HTTP ${status}`;
          reject(new HttpError(msg, status, data));
          return;
        }
        resolve(data);
      });
    };

    xhr.onerror = () => {
      settle(() => {
        reject(new TypeError('Failed to fetch'));
      });
    };

    xhr.ontimeout = () => {
      settle(() => {
        const err = new Error('La requete a expire');
        err.cause = new DOMException('Timeout', 'TimeoutError');
        reject(err);
      });
    };

    xhr.onabort = () => {
      settle(() => {
        const err = new Error('Requete annulee');
        err.cause = userSignal?.aborted ? userSignal.reason : undefined;
        reject(err);
      });
    };

    try {
      xhr.send(payload);
    } catch (e) {
      settle(() => {
        reject(e);
      });
    }
  });
}
